/**
 * Iter 188.50 · Aug 2026 — canonical disposition-outcome label + color.
 *
 * Root cause of the Blackstone activity-row confusion: two rc_call
 * rows shown as "Follow-up" when one was actually "Wrong number"
 * and the other had no disposition. The dispositions collection had
 * `outcome: "wrong_number"` but the frontend fell through to a
 * generic "Follow-up" string because its outcome→label map was
 * incomplete.
 *
 * This module is the single source of truth. Every call-outcome
 * render (Rep Activity report, Lead Focus → Calls tab, dialer
 * timeline card, admin oversight views) should route through
 * `outcomeLabel(outcome, substatus?)` so the vocabulary stays
 * consistent.
 *
 * Deliberately pure — no React, no imports — so it's trivially
 * unit-testable and works in Node/Jest under jsdom.
 */

// Canonical outcome → Title-Case label. Enum values match what the
// backend disposition endpoints emit today (see server.py
// _DispositionBody validation + reports SQL).
const _OUTCOME_LABEL = {
  connected:      "Connected",
  no_answer:      "No answer",
  voicemail:      "Voicemail",
  left_message:   "Left message",
  busy:           "Busy",
  disconnected:   "Disconnected",
  wrong_number:   "Wrong number",
  bad_number:     "Bad number",
  dnc:            "DNC",
  do_not_call:    "DNC",
  not_interested: "Not interested",
  interested:     "Interested",
  callback:       "Callback",
  follow_up:      "Follow-up",
  meeting:        "Meeting scheduled",
  demo:           "Demo scheduled",
  contract:       "Contract sent",
  closed_won:     "Closed won",
  closed_lost:    "Closed lost",
};

// Color hint per outcome so downstream renders don't have to redo
// the mapping. `amber` = attention (no disposition, callback due),
// `green` = positive (connected, meeting, closed won), `red` = terminal
// negative (wrong/bad number, DNC, closed lost), `grey` = neutral
// (no answer, voicemail, busy, unknown).
const _OUTCOME_COLOR = {
  connected:      "green",
  interested:     "green",
  meeting:        "green",
  demo:           "green",
  contract:       "green",
  closed_won:     "green",
  callback:       "amber",
  follow_up:      "amber",
  left_message:   "amber",
  voicemail:      "amber",
  wrong_number:   "red",
  bad_number:     "red",
  dnc:            "red",
  do_not_call:    "red",
  not_interested: "red",
  closed_lost:    "red",
  disconnected:   "grey",
  no_answer:      "grey",
  busy:           "grey",
};

/**
 * Render a disposition outcome as a human-friendly label. Returns the
 * Title-Case canonical label for every enum we know about; for an
 * unknown value, returns the raw string in a form that a human QA
 * eye immediately flags ("wrong_number_maybe") rather than silently
 * mislabeling it as "Follow-up" (the prior bug).
 *
 * @param {string|null|undefined} outcome — the enum from `dispositions.outcome`
 * @param {string|null|undefined} substatus — optional narrower classification
 * @returns {string} Title-Case display label
 */
export function outcomeLabel(outcome, substatus) {
  const key = String(outcome || "").trim().toLowerCase();
  if (!key) return "—";
  if (_OUTCOME_LABEL[key]) return _OUTCOME_LABEL[key];
  // Substatus fallback for cases where outcome is generic but the
  // substatus is meaningful (e.g. outcome=connected, substatus=voicemail).
  const sub = String(substatus || "").trim().toLowerCase();
  if (sub && _OUTCOME_LABEL[sub]) return _OUTCOME_LABEL[sub];
  // Unknown — surface the raw enum so QA notices, not "Follow-up".
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Return one of "green" | "amber" | "red" | "grey" for the outcome,
 * so downstream chip/pill renders stay consistent. Unknown outcomes
 * → "grey".
 */
export function outcomeColor(outcome, substatus) {
  const key = String(outcome || "").trim().toLowerCase();
  if (_OUTCOME_COLOR[key]) return _OUTCOME_COLOR[key];
  const sub = String(substatus || "").trim().toLowerCase();
  if (_OUTCOME_COLOR[sub]) return _OUTCOME_COLOR[sub];
  return "grey";
}

/**
 * Priority ranking used by rapid-redial row collapse (Iter 188.52).
 * When multiple call attempts share a group, the collapsed row
 * displays the outcome with the highest priority number. Positive
 * outcomes bubble up (a rep who eventually connects should get
 * credit for the group), then attention needed, then negative.
 */
export function outcomePriority(outcome) {
  const key = String(outcome || "").trim().toLowerCase();
  const table = {
    closed_won: 100, meeting: 90, demo: 85, contract: 80,
    interested: 75, connected: 70,
    callback: 60, follow_up: 55, left_message: 50, voicemail: 45,
    no_answer: 30, busy: 25, disconnected: 20,
    wrong_number: 15, bad_number: 15, not_interested: 10,
    dnc: 5, do_not_call: 5, closed_lost: 1,
  };
  return table[key] ?? 0;
}
